import { DEFAULT_POSITION_ID, PositionID } from '../../common/Positions/Constants';
import { SIGN_UP_CONFIG, SignUpAccountType } from '../../common/SignUp';

const {
  QUERY_PARAMS: { POSITION_ID, ID_CODE, ACCOUNT_TYPE },
  ACCOUNT_TYPES: { EMPLOYER },
} = SIGN_UP_CONFIG;

const SIGN_UP_STORAGE = {
  AFTER_LOGOUT_FLAG: 'SIGN_UP_AFTER_LOGOUT_FLAG',
  POSITION_ID_AFTER_LOGOUT: 'SIGN_UP_POSITION_AFTER_LOGOUT',
  ID_CODE_AFTER_LOGOUT: 'SIGN_UP_ID_CODE_AFTER_LOGOUT',
  ACCOUNT_TYPE_AFTER_LOGOUT: 'SIGN_UP_ACCOUNT_TYPE_AFTER_LOGOUT',
} as const;

export type SignUpFlowState = {
  position_id?: string;
  id_code?: string;
  signup_account_type?: SignUpAccountType;
};

export type OAuthState = {
  position_id?: PositionID;
  id_code?: string;
  signup_account_type?: SignUpAccountType;
};

const getOptionalSearchParam = (searchParams: URLSearchParams, key: string): string | undefined =>
  searchParams.get(key) || undefined;

const getSignUpAccountType = (value?: string): SignUpAccountType | undefined =>
  value === EMPLOYER ? EMPLOYER : undefined;

export const getSignUpFlowStateFromSearch = (search: string): SignUpFlowState => {
  const searchParams = new URLSearchParams(search);

  return {
    position_id: getOptionalSearchParam(searchParams, POSITION_ID),
    id_code: getOptionalSearchParam(searchParams, ID_CODE),
    signup_account_type: getSignUpAccountType(getOptionalSearchParam(searchParams, ACCOUNT_TYPE)),
  };
};

export const getIsSigningUpAfterLogout = (): boolean =>
  localStorage.getItem(SIGN_UP_STORAGE.AFTER_LOGOUT_FLAG) === SIGN_UP_STORAGE.AFTER_LOGOUT_FLAG;

export const getStoredSignUpFlowState = (): SignUpFlowState => ({
  position_id: localStorage.getItem(SIGN_UP_STORAGE.POSITION_ID_AFTER_LOGOUT) || undefined,
  id_code: localStorage.getItem(SIGN_UP_STORAGE.ID_CODE_AFTER_LOGOUT) || undefined,
  signup_account_type: getSignUpAccountType(
    localStorage.getItem(SIGN_UP_STORAGE.ACCOUNT_TYPE_AFTER_LOGOUT) || undefined
  ),
});

export const storeSignUpFlowStateForLogout = ({
  position_id,
  id_code,
  signup_account_type,
}: SignUpFlowState): void => {
  localStorage.setItem(SIGN_UP_STORAGE.AFTER_LOGOUT_FLAG, SIGN_UP_STORAGE.AFTER_LOGOUT_FLAG);
  localStorage.setItem(SIGN_UP_STORAGE.POSITION_ID_AFTER_LOGOUT, position_id || '');
  localStorage.setItem(SIGN_UP_STORAGE.ID_CODE_AFTER_LOGOUT, id_code || '');
  localStorage.setItem(SIGN_UP_STORAGE.ACCOUNT_TYPE_AFTER_LOGOUT, signup_account_type || '');
};

export const clearStoredSignUpFlowState = (): void => {
  Object.values(SIGN_UP_STORAGE).forEach((key) => localStorage.removeItem(key));
};

export const getSignUpOAuthState = ({
  position_id,
  id_code,
  signup_account_type,
}: SignUpFlowState): OAuthState => {
  const realPositionID: PositionID =
    position_id && position_id in PositionID ? (position_id as PositionID) : DEFAULT_POSITION_ID;

  return {
    position_id: realPositionID,
    id_code,
    signup_account_type,
  };
};

export const isEmployerSignUp = (state?: Partial<OAuthState>): boolean =>
  state?.signup_account_type === EMPLOYER;
